import { Link } from 'react-router-dom';

interface StatCard {
  value: string;
  label: string;
  icon: string;
}

interface Props {
  title: string;
  titleHighlight?: string;
  subtitle: string;
  bgImage: string;
  badge?: string;
  stats?: StatCard[];
}

const defaultStats: StatCard[] = [
  { value: '5.0★', label: 'Google Rating', icon: 'ri-star-fill' },
  { value: '50+', label: 'Client Reviews', icon: 'ri-chat-quote-line' },
  { value: '10+', label: 'Years Experience', icon: 'ri-briefcase-4-line' },
];

export default function SectionHero({ title, titleHighlight, subtitle, bgImage, badge, stats = defaultStats }: Props) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" aria-label="Hero">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('${bgImage}')` }}
      />
      {/* Multi-layer dark overlays for depth */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(110deg, rgba(3,7,14,0.97) 0%, rgba(5,10,18,0.88) 50%, rgba(7,14,28,0.55) 100%)' }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#030710]/80 via-transparent to-[#030710]/30" />

      {/* Gold top accent */}
      <div className="absolute top-0 left-0 w-full h-[3px]" style={{ background: 'linear-gradient(90deg, #c9a84c, #e8cc85, #c9a84c 60%, transparent)' }} />

      {/* Vertical gold left accent */}
      <div className="absolute top-[30%] left-0 w-[3px] h-48 bg-gradient-to-b from-transparent via-[#c9a84c] to-transparent hidden lg:block" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-40 pb-24 lg:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          {/* Left content — spans 7 cols */}
          <div className="lg:col-span-7">
            {/* Badge */}
            {badge && (
              <div className="inline-flex items-center gap-3 mb-8 group">
                <div className="w-10 h-[2px] bg-[#c9a84c]" />
                <span className="text-[#c9a84c] text-[11px] font-bold uppercase tracking-[0.3em]">{badge}</span>
              </div>
            )}

            {/* Headline */}
            <h1
              className="font-bold text-white leading-[1.05] mb-7"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(46px, 6vw, 82px)',
              }}
            >
              {title}
              {titleHighlight && (
                <>
                  {' '}
                  <span
                    className="italic"
                    style={{
                      background: 'linear-gradient(135deg, #c9a84c, #e8cc85)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {titleHighlight}
                  </span>
                </>
              )}
            </h1>

            {/* Gold rule */}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-[3px] bg-[#c9a84c] rounded-full" />
              <div className="w-3 h-[3px] bg-[#c9a84c]/40 rounded-full" />
            </div>

            {/* Subtitle */}
            <p className="text-white/70 leading-[1.8] mb-10" style={{ fontSize: 'clamp(16px, 1.5vw, 21px)' }}>
              {subtitle}
            </p>

            {/* Trust pills */}
            <div className="flex flex-wrap gap-2.5 mb-11">
              {[
                { icon: 'ri-shield-check-fill', text: 'Free Consultation' },
                { icon: 'ri-time-fill', text: 'Same-Day Response' },
                { icon: 'ri-map-pin-2-fill', text: 'Brenham, Texas' },
              ].map((pill) => (
                <div key={pill.text} className="flex items-center gap-2 bg-white/6 backdrop-blur-sm border border-white/12 rounded-full px-4 py-2.5 hover:border-[#c9a84c]/40 transition-colors">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${pill.icon} text-[#c9a84c] text-xs`} />
                  </div>
                  <span className="text-white/80 text-xs font-semibold whitespace-nowrap">{pill.text}</span>
                </div>
              ))}
            </div>

            {/* CTA group */}
            <div className="flex flex-col sm:flex-row gap-3.5">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #e8cc85)', color: '#050a12', fontSize: '15px' }}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-base" />
                </div>
                Get Immediate Legal Help
              </Link>
              <a
                href="tel:9795300250"
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 border-2 border-white/25 text-white font-semibold text-[15px] rounded-xl hover:border-[#c9a84c] hover:text-[#c9a84c] hover:bg-[#c9a84c]/5 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill text-base" />
                </div>
                Call (979) 530-0250
              </a>
            </div>
          </div>

          {/* Right column — stat cards (desktop) */}
          <div className="hidden lg:flex lg:col-span-5 flex-col gap-4">
            {/* Attorney card */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#c9a84c]/30 transition-all duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                  <img
                    src="https://bungerlaw.com/mt-content/uploads/2022/03/dave.jpeg"
                    alt="Attorney Dave Bunger"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-white font-bold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>Dave Bunger</p>
                  <p className="text-[#c9a84c] text-xs font-semibold">Criminal Defense Attorney</p>
                  <p className="text-white/40 text-xs mt-0.5">Brenham, Texas</p>
                </div>
              </div>
              <div className="flex items-center gap-1 mb-2">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-[#c9a84c] text-xs" />
                  </div>
                ))}
                <span className="text-white/45 text-xs ml-1">5.0 Average</span>
              </div>
              <p className="text-white/50 text-sm italic leading-relaxed">"Genuinely fights for every client. Best decision I made was calling Dave."</p>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-3">
              {stats.map((s) => (
                <div key={s.label} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center hover:border-[#c9a84c]/30 transition-all duration-300">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2 bg-[#c9a84c]/15 border border-[#c9a84c]/20 rounded-lg">
                    <i className={`${s.icon} text-[#c9a84c] text-sm`} />
                  </div>
                  <p className="text-white font-black text-xl leading-none mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                  <p className="text-white/40 text-[11px] leading-tight">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Bar association badge */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-5 py-3.5 flex items-center justify-between hover:border-[#c9a84c]/30 transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-[#c9a84c]/15 border border-[#c9a84c]/20 rounded-lg flex-shrink-0">
                  <i className="ri-scales-line text-[#c9a84c] text-base" />
                </div>
                <div>
                  <p className="text-white text-xs font-bold">State Bar of Texas</p>
                  <p className="text-white/40 text-[11px]">Licensed &amp; Active Member</p>
                </div>
              </div>
              <div className="w-6 h-6 flex items-center justify-center bg-green-500/15 border border-green-500/30 rounded-full">
                <i className="ri-check-line text-green-400 text-xs" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#030710] to-transparent" />

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-1.5">
        <div className="w-[1px] h-8 bg-gradient-to-b from-transparent to-white/25" />
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white/25 text-sm" />
        </div>
      </div>
    </section>
  );
}
